import axios from 'axios';
import { redirect, type LoaderFunctionArgs, type Params } from 'react-router';
import { getApiErrorMessage } from '@utils/api-error';

const VALIDATE_SESSION_ENDPOINT = '/api/register/validate-session';
const LOGOUT_ENDPOINT = '/api/register/logout';

const AUTH_LOGIN_PATH = '/register/auth-login';
const AUTH_PATH_PREFIX = '/register/auth-';
const DEFAULT_RETURN_URL = '/register/donate';
const RETURN_URL_PARAM = 'returnUrl';

const basename = import.meta.env.BASE_URL.replace(/\/$/, '');

export interface ISession {
    registrationId: number;
    editKey: string;
}

type TSessionValidationResponse =
    | {
          success: true;
          results: ISession;
      }
    | {
          success: false;
          results?: never;
      };

export const requireSession = async ({ request, params }: LoaderFunctionArgs): Promise<ISession> => {
    const { data } = await axios.post<TSessionValidationResponse>(VALIDATE_SESSION_ENDPOINT, null, {
        headers: { Accept: 'application/json' },
        withCredentials: true,
    });

    const loginUrl = withReturnUrl(AUTH_LOGIN_PATH, toAppPath(request.url, params));

    if (!data.success) {
        throw redirect(loginUrl);
    }

    if (isLinkForOtherRegistration(params, data.results)) {
        await logoutSession();
        throw redirect(loginUrl);
    }

    return data.results;
};

export const withReturnUrl = (path: string, returnUrl: string | null): string =>
    isSafeReturnUrl(returnUrl) ? `${path}?${new URLSearchParams({ [RETURN_URL_PARAM]: returnUrl })}` : path;

export const getReturnUrl = (searchParams: URLSearchParams): string | null => {
    const returnUrl = searchParams.get(RETURN_URL_PARAM);

    return isSafeReturnUrl(returnUrl) ? returnUrl : null;
};

export const getPostLoginUrl = (searchParams: URLSearchParams): string => getReturnUrl(searchParams) ?? DEFAULT_RETURN_URL;

const isLinkForOtherRegistration = ({ registrationId, editKey }: Params, session: ISession): boolean =>
    !!registrationId
    && !!editKey
    && (registrationId !== String(session.registrationId) || editKey !== session.editKey);

// The id/editKey segments are dropped so login never returns to a link for a registration other than the one just authenticated.
const toAppPath = (url: string, { registrationId, editKey }: Params): string => {
    const { pathname, search, hash } = new URL(url);
    const segments = pathname.replace(/\/+$/, '').split('/');
    const linkSegmentCount = [registrationId, editKey].filter(Boolean).length;
    const routePathname = segments.slice(0, segments.length - linkSegmentCount).join('/') || '/';
    const appPathname = basename && routePathname.startsWith(basename) ? routePathname.slice(basename.length) || '/' : routePathname;

    return `${appPathname}${search}${hash}`;
};

// Only in-app paths, so a crafted link can't bounce a freshly authenticated donor off-site.
const isSafeReturnUrl = (returnUrl: string | null): returnUrl is string =>
    !!returnUrl
    && returnUrl.startsWith('/')
    && !returnUrl.startsWith('//')
    && !returnUrl.startsWith('/\\')
    && !returnUrl.startsWith(AUTH_PATH_PREFIX);

export const logoutSession = async (): Promise<void> => {
    await axios.post(LOGOUT_ENDPOINT, null, {
        headers: { Accept: 'application/json' },
        withCredentials: true,
    });
};

export const getSessionError = (error: unknown): string =>
    getApiErrorMessage(error, 'We could not validate your session. Please try again.');
